/**
 * Single source of truth for sidebar defaults, self-service keys, and route permission specs.
 * permKey values match backend JSONField / URL name= (normalize: underscores → spaces).
 */

export const normalizePermKey = (str) =>
    str?.toLowerCase().replace(/_/g, " ").trim();

/** Menus shown without a module permission row (secondary / utility). */
export const FULL_MENU_LABELS = [
    "reports",
    "settings",
    "help",
    "plans",
    "it help desk",
    "ai copilot",
    "integration",
];

/** @deprecated Nav no longer auto-shows these — grant via User Permission instead. */
export const SELF_SERVICE_BY_MODULE = {
    employee: [
        "leave request",
        "reimbursement request",
        "advance salary request",
        "subordinates",
        "employee allowances",
        "employee deductions",
        "resignation",
        "my tasks",
        "branch transfer",
    ],
    attendance: [
        "day attendance",
        "attendance log",
        "shift change requests",
        "my shift change requests",
    ],
    appraisal: ["my appraisals"],
    training: ["my trainings"],
};

/**
 * Build the `defaults` object consumed by buildDynamicNav.
 * Only role-specific admin/branch overrides — NOT employee self-service.
 * Every module screen must be granted in User Permission to appear.
 */
export function buildNavDefaults(ctx) {
    const {
        is_superAdmin,
        isBranchAdmin,
        masterMigrationDefaultLabels = [],
    } = ctx;

    return {
        ...(is_superAdmin
            ? {
                "data migration": ["employee migration", ...masterMigrationDefaultLabels],
                fullMenus: [...FULL_MENU_LABELS],
            }
            : isBranchAdmin
                ? {
                    configuration: [
                        "shift configuration",
                        "holiday",
                        "week off",
                        "payroll period",
                        "subordinate",
                        "salary component",
                        "salary template",
                    ],
                    fullMenus: ["settings", "configuration", "it help desk", "ai copilot"],
                }
                : {
                    // Home + Settings only — every HR screen needs an explicit User Permission grant.
                    fullMenus: ["home", "settings"],
                }),
    };
}

export function isSelfServiceKey(module, permKey) {
    const mod = normalizePermKey(module);
    const key = normalizePermKey(permKey);
    return SELF_SERVICE_BY_MODULE[mod]?.includes(key) ?? false;
}

/** Routes with no permission check (still behind auth). */
const OPEN_ROUTE_PREFIXES = [
    "/home",
    "/employeeprofile",
    "/settings",
    "/calendar",
    "/ai/copilot",
    "/ithelpdesk",
    "/networkError",
    "/employee/resignationrequest/process",
    "/employee/allowances/manage",
    "/employee/deductions/manage",
    "/payroll/runpayrollprocess",
    "/payroll/shortdutyreview",
    "/payroll/perkcriteria/",
    "/attendance/attendancelogChart",
    "/master/leavetype/add",
    "/master/lettertemplate/add",
    "/configuration/shiftconfiguration/add",
    "/configuration/weekoff/add",
    "/configuration/salarytemplate/add",
    "/candidate/add",
    "/intern/add",
];

/**
 * Ordered route rules — first match wins.
 * permKeys + matchAny: allow if any key passes.
 */
export const ROUTE_PERMISSION_RULES = [
    // Administrator
    { pattern: /^\/admin\/companyprofile/, module: "administrator", permKey: "organization" },
    { pattern: /^\/admin\/usergroup/, module: "administrator", permKey: "user_group" },
    { pattern: /^\/admin\/userpermission/, module: "administrator", permKey: "user_permission" },
    { pattern: /^\/admin\/passwordpolicy/, module: "administrator", permKey: "password_policy" },
    { pattern: /^\/admin\/noticeboard/, module: "administrator", permKey: "notice_board" },
    { pattern: /^\/admin\/organizationdocuments/, module: "administrator", permKey: "organization_documents" },

    // Master
    { pattern: /^\/master\/employeetype/, module: "master", permKey: "employee_type" },
    { pattern: /^\/master\/department/, module: "master", permKey: "department" },
    { pattern: /^\/master\/designation/, module: "master", permKey: "designation" },
    { pattern: /^\/master\/city/, module: "master", permKey: "city" },
    { pattern: /^\/master\/block/, module: "master", permKey: "block" },
    { pattern: /^\/master\/floor/, module: "master", permKey: "floor" },
    { pattern: /^\/master\/room/, module: "master", permKey: "room" },
    { pattern: /^\/master\/leavetype/, module: "master", permKey: "leave_type" },
    { pattern: /^\/master\/employeegroup/, module: "master", permKey: "employee_group" },
    { pattern: /^\/master\/reimbursementstype/, module: "master", permKey: "reimbursements_type" },
    { pattern: /^\/master\/assettype/, module: "master", permKey: "asset_type" },
    { pattern: /^\/master\/assetcategory/, module: "master", permKey: "asset_category" },
    { pattern: /^\/master\/assetsubategory/, module: "master", permKey: "asset_sub_category" },
    { pattern: /^\/master\/lettertype/, module: "master", permKey: "letter_type" },
    { pattern: /^\/master\/lettertemplate/, module: "master", permKey: "letter_template" },
    { pattern: /^\/master\/documenttype/, module: "master", permKey: "document_type" },

    // Configuration
    { pattern: /^\/configuration\/shiftconfiguration/, module: "configuration", permKey: "shift_configuration" },
    { pattern: /^\/configuration\/holiday/, module: "configuration", permKey: "holiday" },
    { pattern: /^\/configuration\/weekoff/, module: "configuration", permKey: "week_off" },
    { pattern: /^\/configuration\/payrollperiod/, module: "configuration", permKey: "payroll_period" },
    { pattern: /^\/configuration\/subordinate/, module: "configuration", permKey: "subordinate" },
    { pattern: /^\/configuration\/customconfiguration/, module: "configuration", permKey: "custom_configuration" },
    { pattern: /^\/configuration\/salarycomponent/, module: "configuration", permKey: "salary_component" },
    { pattern: /^\/configuration\/salarytemplate/, module: "configuration", permKey: "salary_template" },
    { pattern: /^\/configuration\/darevision/, module: "configuration", permKey: "da_revision" },

    // Employee
    { pattern: /^\/employee\/viewDetails/, module: "employee", permKey: "employees" },
    { pattern: /^\/intern\/onboarding/, module: "employee", permKey: "intern_onboarding" },
    { pattern: /^\/employee\/add/, module: "employee", permKey: "employees", flags: ["add", "edit"] },
    { pattern: /^\/employee\/quick-register/, module: "employee", permKey: "employees", flag: "add" },
    { pattern: /^\/employee\/?$/, module: "employee", permKey: "employees" },
    { pattern: /^\/employee\/onboarding/, module: "employee", permKey: "employee_onboarding" },
    { pattern: /^\/employee\/internonboarding/, module: "employee", permKey: "intern_onboarding" },
    { pattern: /^\/employee\/leaverequest/, module: "employee", permKey: "leave_request" },
    { pattern: /^\/employee\/reimbursementrequest/, module: "employee", permKey: "reimbursements_request" },
    { pattern: /^\/employee\/advancerequest/, module: "employee", permKey: "advance_salary_request" },
    { pattern: /^\/employee\/overtimerequest/, module: "employee", permKey: "overtime_request" },
    { pattern: /^\/employee\/subordinate/, module: "employee", permKey: "subordinates" },
    { pattern: /^\/employee\/allowances/, module: "employee", permKey: "employee_allowances" },
    { pattern: /^\/employee\/deductions/, module: "employee", permKey: "employee_deductions" },
    { pattern: /^\/employee\/deposits/, module: "employee", permKey: "employee_deposits" },
    { pattern: /^\/employee\/branch-transfer/, module: "employee", permKey: "branch_transfer" },
    { pattern: /^\/employee\/transfer-reporting-manager/, module: "employee", permKey: "transfer_reporting_manager" },
    { pattern: /^\/employee\/resignationrequest/, module: "employee", permKey: "resignation" },
    { pattern: /^\/employee\/absconding/, module: "employee", permKey: "absconding" },
    { pattern: /^\/employee\/approvalinbox/, module: "employee", permKey: "approval_inbox" },
    { pattern: /^\/employee\/timesheet/, module: "employee", permKey: "timesheet" },
    { pattern: /^\/employee\/team\/timesheet/, module: "employee", permKey: "team_timesheet" },

    // Payroll
    { pattern: /^\/payroll\/payrollprocess/, module: "payroll", permKey: "payroll" },
    { pattern: /^\/payroll\/payslips/, module: "payroll", permKey: "payslips" },
    { pattern: /^\/payroll\/tdspreview/, module: "payroll", permKey: "tds_preview" },
    { pattern: /^\/payroll\/tdsconfiguration/, module: "payroll", permKey: "tds_configuration" },
    { pattern: /^\/payroll\/payrollhold/, module: "payroll", permKey: "payroll_hold" },
    { pattern: /^\/payroll\/attendancepenalties/, module: "payroll", permKey: "attendance_penalties" },
    { pattern: /^\/payroll\/perkcriteria/, module: "payroll", permKey: "perk_criteria" },

    // Attendance
    { pattern: /^\/attendance\/dashboard/, module: "attendance", permKey: "attendance_dashboard" },
    { pattern: /^\/attendance\/?$/, module: "attendance", permKey: "attendance_dashboard" },
    { pattern: /^\/attendance\/dayattendance/, module: "attendance", permKey: "day_wise_attendance" },
    { pattern: /^\/attendance\/attendancelog/, module: "attendance", permKey: "attendance_log" },
    { pattern: /^\/attendance\/my-shift-change-requests/, module: "attendance", permKey: "shift_change_requests" },
    { pattern: /^\/attendance\/shift-change-requests/, module: "attendance", permKey: "shift_change_requests" },
    { pattern: /^\/attendance\/punch-discrepancies/, module: "attendance", permKey: "punch_discrepancies" },
    { pattern: /^\/attendance\/penalty-appeals/, module: "attendance", permKey: "penalty_appeals" },

    // Asset management
    { pattern: /^\/assetmanagement\/settings/, module: "asset_management", permKey: "asset_request" },
    { pattern: /^\/assetmanagement\/assetrequest/, module: "asset_management", permKey: "asset_request" },
    { pattern: /^\/assetmanagement\/assetregistry/, module: "asset_management", permKey: "asset_registry" },
    { pattern: /^\/assetmanagement\/assetemployeeaudit/, module: "asset_management", permKey: "asset_employee_audit" },

    // Roster — per-screen guards (order matters: specific paths first)
    { pattern: /^\/roster\/report/, module: "reports", permKey: "roster_report" },
    { pattern: /^\/roster\/duty-roster/, module: "roster", permKey: "duty_roster" },
    { pattern: /^\/roster\/add/, module: "roster", permKey: "roster" },
    { pattern: /^\/roster\/?$/, module: "roster", permKey: "roster" },

    // Training (NABH feature flag) — overdue first (employee OR admin)
    { pattern: /^\/training\/overdue/, module: "training", permKeys: ["my_trainings", "assign_training"], featureFlag: "nabh" },
    { pattern: /^\/training\/(add|edit|task)/, module: "training", permKey: "assign_training", featureFlag: "nabh" },
    { pattern: /^\/training\/team/, module: "training", permKey: "team_trainings", featureFlag: "nabh" },
    { pattern: /^\/training/, module: "training", permKey: "my_trainings", featureFlag: "nabh" },

    // Internship
    { pattern: /^\/internship\/programs/, module: "internship", permKey: "internship_programs" },
    { pattern: /^\/internship\/enrolments/, module: "internship", permKey: "internship_enrolments" },
    { pattern: /^\/internship\/mine/, module: "internship", permKey: "my_internship" },

    // Appraisal
    { pattern: /^\/appraisal\/team-review/, module: "appraisal", permKey: "team_appraisals", roleGate: "rm" },
    { pattern: /^\/appraisal\/kpi-manager/, module: "appraisal", permKey: "appraisal_kpis", roleGate: "rmOrHr" },
    { pattern: /^\/appraisal\/cycles/, module: "appraisal", permKey: "appraisal_cycles", roleGate: "hr" },
    { pattern: /^\/appraisal\/final-approval/, module: "appraisal", permKey: "appraisal_approval", roleGate: "hr" },
    { pattern: /^\/appraisal\/calibration/, module: "appraisal", permKey: "appraisal_approval", roleGate: "hr" },
    { pattern: /^\/appraisal\/nine-box/, module: "appraisal", permKey: "team_appraisals", roleGate: "rmOrHr" },
    { pattern: /^\/appraisal\/competencies/, module: "appraisal", permKey: "appraisal_kpis", roleGate: "hr" },

    { pattern: /^\/appraisal/, module: "appraisal", permKey: "my_appraisals" },

    // Grievance
    { pattern: /^\/grievance\/my-grievances/, module: "grievance", permKey: "my_grievances" },
    { pattern: /^\/grievance\/inbox/, module: "grievance", permKey: "grievance_inbox" },
    { pattern: /^\/grievance\/categories/, module: "grievance", permKey: "grievance_categories" },
    { pattern: /^\/grievance\/?$/, module: "grievance", permKey: "grievance_dashboard" },

    // Reports index is open; individual reports checked below
    { pattern: /^\/reports\/?$/, open: true },

    // Reports (module: reports)
    { pattern: /^\/reports\/branch-wise-activity/, module: "reports", permKey: "branch_wise_activity_report", orgAdminOnly: true },
    { pattern: /^\/reports\/daywise-attendance/, module: "reports", permKey: "day_wise_attendance" },
    { pattern: /^\/reports\/detailed-attendance/, module: "reports", permKey: "day_wise_attendance" },
    { pattern: /^\/reports\/department-wise-activity/, module: "reports", permKey: "day_wise_attendance" },
    { pattern: /^\/reports\/discrepancy-report/, module: "reports", permKey: "day_wise_attendance" },
    { pattern: /^\/reports\/permission-punch-report/, module: "reports", permKey: "day_wise_attendance" },
    { pattern: /^\/reports\/monthwise-attendance/, module: "reports", permKey: "month_wise_attendance" },
    { pattern: /^\/reports\/salary-process-bank/, module: "reports", permKey: "salary_process_bank" },
    { pattern: /^\/reports\/employee-salary-summary/, module: "reports", permKey: "employee_salary_summary" },
    { pattern: /^\/reports\/payroll-detailed-summary/, module: "reports", permKey: "payroll_detailed_summary" },
    { pattern: /^\/reports\/employee-esi-statement/, module: "reports", permKey: "employee_esi_statement" },
    { pattern: /^\/reports\/employee-pf-statement/, module: "reports", permKey: "employee_pf_statement" },
    { pattern: /^\/reports\/muster-roll/, module: "reports", permKey: "muster_roll" },
    { pattern: /^\/reports\/employee-credits-debits/, module: "reports", permKey: "employee_earning_deduction" },
    { pattern: /^\/reports\/attendance-correction/, module: "reports", permKey: "attendance_correction" },
    { pattern: /^\/reports\/subordinates-report/, module: "reports", permKey: "subordinates" },
    { pattern: /^\/reports\/roster-report/, module: "reports", permKey: "roster_report" },
    { pattern: /^\/reports\/leave-permission-report/, module: "reports", permKey: "leave_permission_report" },
    { pattern: /^\/reports\/short-duty/, module: "reports", permKey: "short_duty_report" },
    { pattern: /^\/reports\/fine-report/, module: "reports", permKey: "fine_report" },
    { pattern: /^\/reports\/document-upload-report/, module: "reports", permKey: "document_upload_report" },

    // Integration — admin-only; page shows a setup prompt when eMedHub is not linked.
    { pattern: /^\/integration/, open: true, requireAdmin: true },

    // Data migration — admin only
    { pattern: /^\/datamigration/, requireAdmin: true },
];

const readBool = (key) =>
    JSON.parse(sessionStorage.getItem(key) || "false");

/** Roles as the session records them. Lives here rather than in
 *  usePermissionAccess so the sidebar can reach it: CommonFunctions cannot
 *  import from that hook without a cycle.
 *
 *  Org admin is LoginUser.is_admin → session is_super_admin.
 *  Employee.is_admin (session is_admin) is department admin, not a skip.
 */
export const readSessionRoles = () => ({
    isAdmin: readBool("is_super_admin") || readBool("is_system_admin"),
    isSystemAdmin: readBool("is_system_admin"),
    isHR: readBool("is_admin") || readBool("is_super_admin"),
    isRM: sessionStorage.getItem("reportingManager") === "true",
    isAuthenticated: !!sessionStorage.getItem("token"),
    isEmployee: !!sessionStorage.getItem("employee_id"),
    isOrgAdmin: readBool("is_super_admin"),
});

export function passesRoleGate(roleGate, roles) {
    if (!roleGate) return true;
    if (roleGate === "rm") return roles.isRM;
    if (roleGate === "hr") return roles.isHR;
    if (roleGate === "rmOrHr") return roles.isRM || roles.isHR;
    return true;
}

/** Whether this href's route would admit the current user on role grounds.
 *  The sidebar asks this so it stops offering links the router refuses. */
export function passesRouteRoleGate(href) {
    if (!href) return true;
    const spec = matchRoutePermission(href);
    if (!spec || !spec.roleGate) return true;
    return passesRoleGate(spec.roleGate, readSessionRoles());
}

export function matchRoutePermission(pathname) {
    const path = pathname.replace(/\/$/, "") || "/";

    if (OPEN_ROUTE_PREFIXES.some((prefix) => path === prefix || path.startsWith(prefix))) {
        return { open: true };
    }

    for (const rule of ROUTE_PERMISSION_RULES) {
        if (rule.pattern.test(path)) {
            return rule;
        }
    }

    return null;
}
